import {
  IconButton,
  Checkbox,
  HStack,
  Box,
  Flex,
  Tooltip
} from '@chakra-ui/react'
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react'
import Highlighter from 'react-highlight-words'
import { AiOutlineSound, AiFillFile, AiOutlineStock } from 'react-icons/ai'
import {
  BsCheck2Circle,
  BsCheckCircleFill,
  BsExclamationTriangle,
  BsCalculator
} from 'react-icons/bs'
import {
  FaBoxOpen,
  FaSortAmountDownAlt,
  FaCogs,
  FaBalanceScale,
  FaShieldAlt,
  FaBan
} from 'react-icons/fa'
import {
  IoIosCloseCircle,
  IoLogoBuffer,
  IoMdCloseCircleOutline
} from 'react-icons/io'

import useMrpPurchase from '@hook/mrp/use-purchase'
import useTable from '@hook/shared/use-table'

import { ITableGridRow } from '@base/@types/components/Layout/TableGrid'

import './styles.css'

import { ReactComponent as IncrementStock } from '../../../../../Icons/increment-stock1.svg'
import { ReactComponent as StockMinor } from '../../../../../Icons/stockMinor1.svg'

// eslint-disable-next-line import-helpers/order-imports
import CalculoDetalheTabsModal from '@component/Modules/Mrp/Modals/VendaMediaMovelDetalhe'

export interface ICountry {
  value: number
  country: 'pt-br'
  minFraction?: number
  maxFraction?: number
}
const TableGridPurshase: FC<{
  [key: string]: any
  [indexData: number]: number
  onUpdateWanted?: (estoqueId: number, quantidade: number) => void
}> = memo(({ data, onUpdateWanted }) => {
  const { actions, columns, query, context } = useTable()
  const {
    handleUpdateWanted: defaultHandleUpdateWanted,
    getBuffer,
    possuirBuffer,
    lastSearchVmm
  } = useMrpPurchase()
  const [isCalculoOpen, setIsCalculoOpen] = useState(false)

  const handleUpdateWanted = context?.isVisualizarPedido
    ? onUpdateWanted || context?.onUpdateWanted
    : onUpdateWanted || context?.onUpdateWanted || defaultHandleUpdateWanted

  // Desabilita apenas quando não há função de update disponível
  const isDisabled = !handleUpdateWanted

  // Último valor enviado nesta edição: evita reenviar no blur que segue o Enter
  const lastSentRef = useRef<string | null>(null)

  const handleBlur = useCallback(
    (text: string) => {
      if (!handleUpdateWanted) return
      const typed = text.trim()
      const current = String(data['compra-desejada'] ?? '').replace(/^[TF]/, '')
      // Blur sem mudança não dispara PUT
      if (typed === current || typed === lastSentRef.current) return
      lastSentRef.current = typed
      handleUpdateWanted(data['estoque-id'], typed)
    },
    [handleUpdateWanted, data]
  )
  const isBuffer = useMemo(
    () =>
      possuirBuffer.some(
        x =>
          x.storeId === data['centro-id'] && x.produtoId === data['produto-id']
      ),
    [possuirBuffer, data]
  )
  const rows = useMemo<ITableGridRow[]>(() => {
    const res = []
    for (const column of columns) {
      if (column.columns && column.columns.length > 0) {
        for (const row of column.columns) {
          res.push({
            id: row.id,
            value: data[row.id],
            align: row.align || 'left',
            isNumeric: row.isNumeric
          })
        }
      } else {
        res.push({
          id: column.id!,
          value: data[column.canPrint ? column.id! : column.group],
          align: column.align || 'left'
        })
      }
    }
    return res
  }, [columns, data])

  return (
    <tr>
      {actions && actions.length > 0 && (
        <td>
          <HStack
            spacing={2}
            w="100%"
            alignItems="center"
            justifyContent="center"
          >
            {actions.map((action, index) => {
              if (action.type === 'icon') {
                return (
                  <IconButton
                    key={`table-action-${index}`}
                    aria-label="Abrir modal"
                    icon={action.icon}
                    size="xs"
                    onClick={() => action.onSelect!(data)}
                    _hover={{
                      bg: 'yellow.200'
                    }}
                    _active={{
                      bg: 'yellow.100'
                    }}
                  />
                )
              }
              if (action.type === 'iconBuffer' && isBuffer) {
                return (
                  <IconButton
                    key={`table-action-${index}`}
                    aria-label="Abrir modal"
                    icon={<IoLogoBuffer />}
                    size="xs"
                    onClick={() => {
                      getBuffer(data['centro-id'], data['produto-id'])
                    }}
                    _hover={{
                      bg: 'yellow.200'
                    }}
                    _active={{
                      bg: 'yellow.100'
                    }}
                  />
                )
              }

              if (action.type === 'iconCalculo') {
                const etapasSaida: string[] = data['calculo-saida-diaria'] || []
                const etapasQuantidade: string[] =
                  data['calculo-quantidade'] || []
                const temDados =
                  etapasSaida.length > 0 || etapasQuantidade.length > 0

                return (
                  <>
                    {isCalculoOpen && (
                      <CalculoDetalheTabsModal
                        isOpen={isCalculoOpen}
                        onClose={() => setIsCalculoOpen(false)}
                        produtoId={String(data['produto-id'])}
                        lojaId={String(data['centro-id'])}
                        etapasSaida={etapasSaida}
                        etapasQuantidade={etapasQuantidade}
                        defaultTab={lastSearchVmm ? 'vmm' : 'lambda'}
                        showVmmTab={lastSearchVmm}
                      />
                    )}
                    <Tooltip
                      key={`table-action-${index}`}
                      label="Detalhamento do cálculo"
                      placement="top"
                      fontSize="xs"
                      hasArrow
                    >
                      <IconButton
                        aria-label="Detalhamento do cálculo"
                        icon={<BsCalculator />}
                        size="xs"
                        variant="ghost"
                        color={temDados ? '#1d5840' : 'gray.300'}
                        onClick={() => setIsCalculoOpen(true)}
                        _hover={{ bg: '#1d584018', color: '#1d5840' }}
                        _active={{ bg: '#1d584030' }}
                      />
                    </Tooltip>
                  </>
                )
              }

              if (action.type === 'checkbox') {
                if (action.isMulti) {
                  return (
                    <Checkbox
                      key={`table-action-${index}`}
                      colorScheme="red"
                      isChecked={action.selecteds.includes(data['id-id'])}
                      borderColor="blackAlpha.400"
                      alignItems="center"
                      onChange={() => {
                        if (action.onChange) {
                          action.onChange(data)
                        }
                      }}
                    />
                  )
                } else {
                  return (
                    <Checkbox
                      key={`table-action-${index}`}
                      colorScheme="yellow"
                      isChecked={action.isChecked}
                      borderColor="blackAlpha.400"
                      alignItems="center"
                      isDisabled={
                        action.selecteds !== 0
                          ? action.selecteds !== data['id-id']
                          : false
                      }
                      onChange={() => {
                        if (action.onChange) {
                          action.onChange(data)
                        }
                      }}
                    />
                  )
                }
              }
            })}
          </HStack>
        </td>
      )}
      {rows.map(({ id, value, align, isNumeric }, key) => (
        <td
          id={id}
          key={`${id}-${key}`}
          align={align || 'left'}
          style={{
            position: 'relative',
            padding: id === 'compra-desejada' ? '8px' : undefined,
            background: id === 'compra-desejada' ? '#FEFFC7' : undefined,
            cursor:
              id === 'compra-desejada' && !data.disabled ? 'text' : undefined
          }}
          className={
            id === 'compra-desejada' ? 'custom td-element' : 'td-element'
          }
        >
          {(() => {
            if (id === 'compra-desejada') {
              return (
                <Flex
                  width="100%"
                  minHeight="24px"
                  alignItems="center"
                  onClick={e => {
                    if (!data.disabled) {
                      const editableBox = e.currentTarget.querySelector(
                        '[contenteditable="true"]'
                      ) as HTMLElement
                      if (editableBox) {
                        editableBox.focus()
                        const range = document.createRange()
                        range.selectNodeContents(editableBox)
                        const selection = window.getSelection()
                        selection?.removeAllRanges()
                        selection?.addRange(range)
                      }
                    }
                  }}
                  cursor={!data.disabled ? 'text' : 'default'}
                >
                  {value.substring(0, 1) === 'T' ? (
                    <BsExclamationTriangle
                      color="red"
                      style={{ marginRight: '4px', flexShrink: 0 }}
                    />
                  ) : null}
                  <Box
                    contentEditable={!data.disabled && !isDisabled}
                    onFocus={() => {
                      lastSentRef.current = null
                    }}
                    onBlurCapture={e =>
                      !isDisabled &&
                      !data.disabled &&
                      handleBlur(e.currentTarget.textContent || '')
                    }
                    onKeyDown={e => {
                      if (isDisabled || data.disabled) {
                        e.preventDefault()
                        return
                      }
                      if (
                        e.keyCode === 46 ||
                        e.keyCode === 8 ||
                        (e.keyCode > 95 && e.keyCode < 106) ||
                        e.keyCode === 37 ||
                        e.keyCode === 39
                      ) {
                        // let it happen, don't do anything
                      } else {
                        if (e.keyCode < 48 || e.keyCode > 57) {
                          e.preventDefault()
                        }
                      }
                      if (e.key === '-') {
                        e.preventDefault()
                      }
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleBlur(e.currentTarget.textContent || '')
                        e.currentTarget.blur()
                      }
                    }}
                    width="100%"
                    minHeight="20px"
                    outline="none"
                    _focus={{
                      outline: '2px solid #3182ce',
                      borderRadius: '2px'
                    }}
                  >
                    {String(value).replace(/^[TF]/, '')}
                  </Box>
                </Flex>
              )
            } else if (id === 'recomendacao-tipo-alerta') {
              return <AlertIcon value={value} />
            } else {
              return (
                <div className="body__th-value">
                  <Box
                    justifyContent={isNumeric ? 'center' : null}
                    textAlign={isNumeric ? 'center' : null}
                  >
                    {query.length > 0 ? (
                      <Highlighter
                        searchWords={String(query).split(' ')}
                        textToHighlight={String(value)}
                      />
                    ) : (
                      value
                    )}
                  </Box>
                </div>
              )
            }
          })()}
        </td>
      ))}
    </tr>
  )
})

const AlertIcon: FC<{ value: any }> = memo(({ value }) => {
  const status = parseInt(value, 10)
  switch (status) {
    case 0:
      return <BsCheck2Circle title="Ideal" />
    case 1:
      return <FaBoxOpen title="Pacote" />
    case 2:
      return <AiOutlineStock title="Estoque Máximo" />
    case 3:
      return <FaSortAmountDownAlt title="Compra Mínima" />
    case 4:
      return <FaCogs title="Opção de Compra" />
    case 5:
      return <IoIosCloseCircle title="Sem Estoque" />
    case 6:
      return <IoMdCloseCircleOutline title="Indisponível" />
    case 7:
      return <FaBalanceScale title="Compra Mínima > Estoque Máximo" />
    case 8:
      return <FaShieldAlt title="Cobertura < Estoque Segurança" />
    case 9:
      return <FaBan title="Compra Desnecessária" />
    case 10:
      return <StockMinor title="Estoque Arbitrado" />
    case 11:
      return <IncrementStock title="Lote Múltiplo" />
    case 12:
      return <IncrementStock title="Produto Similar" />
    case 13:
      return <AiFillFile title="Produto Novo" />
    case 14:
      return <AiOutlineSound title="Analisar" />
    case 15:
      return <BsCheckCircleFill title="OK" />
    case 16:
      return <StockMinor title="Estoque Baixo CD" />
    case 17:
      return <StockMinor title="Estoque Mínimo" />
    case 18:
      return <IncrementStock title="Incremento Estoque" />
    default:
      return null
  }
})

AlertIcon.displayName = 'AlertIcon'
TableGridPurshase.displayName = 'TableGridPurshase'

export default TableGridPurshase
